import { AfterViewInit, Component, Input } from "@angular/core";
import { TradableListService } from "../../services/currency/tradable_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { TranslationService } from "@core-services/translation.service";
import { IndexAllocation } from "@models/tradable/index-allocation";
import { config } from "@config";
import { IndexService } from "../../services/currency/index.service";
import { Fiat } from "@models";
import { IndexWalletService } from "../../services/wallet/index_wallet.service";
import { Index } from "@models/asset/index/index.model";
import { HOLDINGS_DONUT_NORTH_POSITION, HOLDINGS_DONUT_RADIUS, VISIBLE_LIST_ITEMS } from "@helpers/constants";
import { DonutChartSegment } from "../../../core/components/bp-donut-chart/bp-donut-chart.component";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { AccountService } from "../../../account/services/account.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
	selector: "index-holdings",
	templateUrl: "./index-holdings.component.html",
	styleUrls: ["./index-holdings.component.scss"],
	standalone: false
})
export class IndexHoldingsComponent implements AfterViewInit {
	@Input("tradable")
	public tradable: Tradable = null;

	@Input("isWalletHoldings")
	private isWalletHoldings: boolean = false;

	private indexAllocations: IndexAllocation[] = [];
	private indexAllocationsFiltered: IndexAllocation[] = [];
	private holdingsModelMap: Map<string, DonutChartSegment> = new Map();
	private donutRadius: number = HOLDINGS_DONUT_RADIUS;
	private emptyWallet: boolean = true;
	private viewAll: boolean = false;
	public indexProvider: string = "";
	public indexCurrencySymbol: string = "";

	public constructor(
		private tradableListService: TradableListService,
		private indexService: IndexService,
		private indexWalletService: IndexWalletService,
		private apicallService: ApicallService,
		private translationService: TranslationService,
		private accountService: AccountService
	) {}

	public ngAfterViewInit(): void {
		if (!this.tradable) {
			return;
		}

		if (this.isWalletHoldings) {
			this.indexWalletService
				.getList()
				.pipe(untilDestroyed(this))
				.subscribe(() => this.fetchWalletHoldingsData());
		} else {
			this.emptyWallet = false;
			const index: Index = this.indexService.getItemById(this.tradable.getId());
			this.indexAllocations = index.getIndexAllocations();
			this.indexProvider = index.getIndexProviderName();
			this.indexCurrencySymbol = index.getIndexOriginalFiatSymbol();
			this.initDonutChart();
		}
	}

	private fetchWalletHoldingsData(): void {
		this.apicallService
			.get("indexes/" + this.tradable.getId() + "/index-holdings", new ApicallOption(IndexAllocation))
			.subscribe((res) => {
				this.emptyWallet = res.length === 0;
				this.indexAllocations = res;
				this.indexAllocationsFiltered = res.slice(0, VISIBLE_LIST_ITEMS);
				this.initDonutChart();
			});
	}

	private initDonutChart(): void {
		let dashOffsetStart: number = HOLDINGS_DONUT_NORTH_POSITION;
		this.indexAllocations.forEach((item: IndexAllocation) => {
			const dashArray: string = this.getAllocationPercent(item) + " " + (100 - +this.getAllocationPercent(item));
			const dashColor: string = this.tradableListService.getItemById(item.getAssetId()).getColor();

			this.holdingsModelMap.set(item.getAssetId(), {
				dashArray: [dashArray],
				dashOffset: "" + dashOffsetStart,
				dashColor: dashColor
			});

			dashOffsetStart = dashOffsetStart - +this.getAllocationPercent(item); // Takes end-position of the slice before it into account and moves the next slice further along the donut
		});
	}

	public isPriceListChart(): boolean {
		return !this.isWalletHoldings;
	}

	public showIndexChart(): boolean {
		return !this.emptyWallet && !!this.tradable;
	}

	public getLegalLinkUrl(): string {
		return (config.baseUrl + config.externalPaths["index"]["prospectus"]).replace(
			"%s",
			TranslationService.getLanguageIsoCode()
		);
	}

	public openPriceInfoLink(): void {
		const priceUrl: string =
			config.baseUrl +
			TranslationService.getLanguageIsoCode() +
			"/prices/crypto-index/" +
			this.tradable.getSymbol(true);
		window.open(priceUrl, "_blank");
	}

	public getTradableFromItem(item: IndexAllocation): Tradable {
		return this.tradableListService.getItemById(item.getAssetId());
	}

	public getItemBalance(item: IndexAllocation, wantFiat: boolean = false): string {
		return wantFiat
			? "" + convertTradableToFiat(this.getTradableFromItem(item), +item.getBalance(), this.getFiat())
			: item.getBalance();
	}

	public getFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public getVisibleIndexAllocations(): IndexAllocation[] {
		return (this.viewAll && this.useViewAll()) || !this.useViewAll() || !this.isWalletHoldings
			? this.getIndexAllocations()
			: this.indexAllocationsFiltered;
	}

	public useViewAll(): boolean {
		return this.indexAllocations.length > VISIBLE_LIST_ITEMS;
	}

	public getIndexAllocations(): IndexAllocation[] {
		return this.indexAllocations;
	}

	public getAllocationPercent(item: IndexAllocation): string {
		return "" + (+item.getAllocationPercent() * 100).toFixed(2);
	}

	public getAllocationPercentDisplay(item: IndexAllocation): string {
		return +this.getAllocationPercent(item) > 0 ? this.getAllocationPercent(item) : "< " + 0.01;
	}

	public getDonutRadius(): number {
		return this.donutRadius;
	}

	public getMapItemByAllocation(item: IndexAllocation): DonutChartSegment {
		return this.holdingsModelMap.get(item.getAssetId());
	}

	public getAllMapItemByAllocation(allocations: IndexAllocation[]): DonutChartSegment[] {
		const donutChartSegments: DonutChartSegment[] = [];
		allocations.forEach((allocation) => {
			donutChartSegments.push(this.holdingsModelMap.get(allocation.getAssetId()));
		});
		return donutChartSegments;
	}

	public onClickViewAll(): void {
		this.viewAll = !this.viewAll;
	}

	public getViewAll(): boolean {
		return this.viewAll;
	}

	public showIndexOnlyInfo(): boolean {
		if (!this.hasIndexOnlyCoins()) {
			return false;
		}
		return this.getViewAll() || this.isPriceListChart();
	}

	public getIndexOnlyUrl(): string {
		return this.translationService.getHdLink() + config.externalPaths["index"]["indexOnly"];
	}

	public hasIndexOnlyCoins(): boolean {
		return !!this.getIndexAllocations().find((item) => this.getTradableFromItem(item).isIndexOnly());
	}
}

export class HoldingsDonutSlice {
	private dasharray: string = ""; // Defines the area that is filled by the slice
	private dashOffset: string = ""; // Defines the offset of the slice in regards to the the north-zero-point
	private dashColor: string = "";

	public constructor(dasharray: string, dashOffset: string, dashColor: string) {
		this.setDashArray(dasharray);
		this.setDashOffset(dashOffset);
		this.setDashColor(dashColor);
	}

	public getDashOffset(): string {
		return this.dashOffset;
	}

	public getDasharray(): string {
		return this.dasharray;
	}

	public getDashColor(): string {
		return this.dashColor;
	}

	public setDashArray(dasharray: string): void {
		this.dasharray = dasharray;
	}

	public setDashOffset(dashOffset: string): void {
		this.dashOffset = dashOffset;
	}

	public setDashColor(dashColor: string): void {
		this.dashColor = dashColor;
	}
}
